import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { HeatStressPlansFeatures, HEAT_STRESS_SUBSCRIPTIONS, getHeatStressTier } from './';
import { useDoModal } from '../user/redux/doModal';
import './HeatStressSubscriptionSelector.less';

function HeatStressSubscriptionSelector({
  onUpgrade,
  user
}) {
  const history = useHistory();
  const { doModal } = useDoModal();

  // Determine current Heat Stress tier from user subscriptions
  const currentTier = useMemo(() => {
    return getHeatStressTier(user)
  }, [user]);

  // Handle upgrade button click
  const handleUpgrade = (tier) => {
    if (onUpgrade) {
      // Use provided callback (for controlled mode)
      onUpgrade(tier);
    } else {
      // Handle navigation internally (for standalone mode)
      const subscription = `heat-stress-${tier}-monthly`;
      doModal(null); // Close any open modal before navigating
      history.push(`/subscriptions/checkout?subscription=${subscription}`);
    }
  };

  // Determine button state for each tier
  const getButtonState = (tier) => {
    if (currentTier === tier) {
      return { label: 'Current', disabled: true };
    }
    // Determine if it's an upgrade or downgrade
    const tierOrder = ['free', 'standard', 'pro', 'enterprise'];
    const currentIndex = tierOrder.indexOf(currentTier);
    const targetIndex = tierOrder.indexOf(tier);

    if (targetIndex > currentIndex) {
      return { label: 'Upgrade', disabled: false };
    } else {
      return { label: 'Downgrade', disabled: false };
    }
  };

  const standardBtn = getButtonState('standard');
  const proBtn = getButtonState('pro');
  const enterpriseBtn = getButtonState('enterprise');

  // Get pricing from HEAT_STRESS_SUBSCRIPTIONS
  const standardSub = HEAT_STRESS_SUBSCRIPTIONS.find(sub => sub.productName === 'heat-stress-standard-monthly');
  const proSub = HEAT_STRESS_SUBSCRIPTIONS.find(sub => sub.productName === 'heat-stress-pro-monthly');
  const enterpriseSub = HEAT_STRESS_SUBSCRIPTIONS.find(sub => sub.productName === 'heat-stress-enterprise-monthly');

  return (
    <div className="payment-heat-stress-subscription-selector">
      <div className='product-tiers'>
        <div className={classNames('tier tier-standard', {
          active: currentTier === 'standard'
        })}>
          <div className='top'>
            <h3>Heat Stress Standard</h3>
            <h2>
              ${standardSub?.price || '16.67'}
              <span className='per-month'>/month</span>
            </h2>
            <button
              className='btn btn-primary'
              disabled={standardBtn.disabled}
              onClick={() => handleUpgrade('standard')}
            >
              {standardBtn.label}
            </button>
          </div>
          <div className='bottom'>
            Ideal for families who need reliable heat stress alerts to keep loved ones safe during extreme weather.
          </div>
        </div>

        <div className={classNames('tier tier-pro', {
          active: currentTier === 'pro'
        })}>
          <div className='top'>
            <h3>Heat Stress Pro</h3>
            <h2>
              ${proSub?.price || '24.99'}
              <span className='per-month'>/month</span>
            </h2>
            <button
              className='btn btn-primary'
              disabled={proBtn.disabled}
              onClick={() => handleUpgrade('pro')}
            >
              {proBtn.label}
            </button>
          </div>
          <div className='bottom'>
            Perfect for small teams and businesses who need enhanced SMS capacity and broader alert distribution.
          </div>
        </div>

        <div className={classNames('tier tier-enterprise', {
          active: currentTier === 'enterprise'
        })}>
          <div className='top'>
            <h3>Heat Stress Enterprise</h3>
            <h2>
              ${enterpriseSub?.price || '33.33'}
              <span className='per-month'>/month</span>
            </h2>
            <button
              className='btn btn-primary'
              disabled={enterpriseBtn.disabled}
              onClick={() => handleUpgrade('enterprise')}
            >
              {enterpriseBtn.label}
            </button>
          </div>
          <div className='bottom'>
            Best for large teams and organizations requiring maximum SMS capacity and comprehensive alert coverage.
          </div>
        </div>
      </div>

      <div className='features'>
        <h3>Heat Stress Plan Features</h3>
        <HeatStressPlansFeatures
          onClick={handleUpgrade}
          currentTier={currentTier}
        />
      </div>
    </div>
  );
}

HeatStressSubscriptionSelector.propTypes = {};
HeatStressSubscriptionSelector.defaultProps = {};
HeatStressSubscriptionSelector.displayName = 'HeatStressSubscriptionSelector';

export default HeatStressSubscriptionSelector;
